import React, { useState, useEffect, useCallback, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Box, CssBaseline, CircularProgress } from "@mui/material";
import { ClinicalThemeProvider } from "./components/shared/ThemeProvider";
import { Toaster, toast } from "sonner";
// Import types and utilities
import type { User, MenuItem } from "./types/Sidebar.type";
import type { PasswordExpiryInfo } from "./types/auth.type";
import type { Study } from "./pages/StudyManagement/types/studyManagement.types";
import { container } from "./container/DependencyContainer";
// Helper function to check if a path exists in the menu
const isPathInMenu = (menu: MenuItem[], path: string): boolean => {
  for (const item of menu) {
    if (item.path && path.startsWith(item.path)) {
      return true;
    }
    if (item.children && item.children.length > 0) {
      if (isPathInMenu(item.children, path)) {
        return true;
      }
    }
  }
  return false;
};
// Helper function to get the first navigable menu item
const getFirstMenuPath = (menu: MenuItem[]): string | null => {
  for (const item of menu) {
    // If item has children, recursively find first navigable item
    if (item.children && item.children.length > 0) {
      const childPath = getFirstMenuPath(item.children);
      if (childPath) {
        return childPath;
      }
    }
    // If item is active and has a path, return it
    if (item.active && item.path) {
      return item.path;
    }
  }
  return null;
};
import {
  getToken,
  clearToken,
  getStoredUser,
  setStoredUser,
} from "./utils/auth";
import {
  STUDY_CONTEXT_REFRESH_EVENT,
  appendStudyRoleToUser,
  buildAvailableContextStudies,
  buildUserStudyRolesMap,
  dispatchStudyChanged,
  getPlatformRoleName,
  isPlatformOnlyRole,
  isPmRole,
  getAnyPmOrCtaRole,
  NO_STUDY_SENTINEL,
  type StudyContextRefreshDetail,
} from "./utils/studyContext";
import { useDrawer } from "./hooks/useDrawer";
import { useInactivityLogout } from "./hooks/useInactivityLogout";
import { MenuRepository } from "./repositories/MenuRepository";
import { clearServiceWorkerAndCacheStorage } from "./utils/pwa";
import { BookOpen, Building2 } from "lucide-react";
// Restricted menus for PM / CTA when no study is selected
const PM_NO_STUDY_MENU: MenuItem[] = [
  {
    id: "study-management",
    name: "Study Management",
    icon: BookOpen,
    path: "/studies",
    active: true,
  },
];
const CTA_NO_STUDY_MENU: MenuItem[] = [
  {
    id: "site-management",
    name: "Site Management",
    icon: Building2,
    path: "/sites",
    active: true,
  },
];
// Import components
import {
  LoginPage,
  RegistrationPage,
  ResetPasswordPage,
  ChangeExpiredPasswordPage,
} from "./pages";

import PWAUpdateNotification from "./components/shared/PWAUpdateNotification";
import InactivityWarningDialog from "./components/shared/InactivityWarningDialog";
// Standalone Admin Tools (easily removable - see src/pages/AdminTools/README for removal instructions)
import { TenantAdminInvitePage } from "./pages/AdminTools";
const CDASignPage = React.lazy(() => import("./pages/CDA/CDASignPage"));
const FeasibilityQuestionnairePage = React.lazy(
  () => import("./pages/SiteFeasibility/FeasibilityQuestionnairePage"),
);
const FeasibilityQuestionnaireResponsePage = React.lazy(
  () => import("./pages/SiteFeasibility/FeasibilityQuestionnaireResponsePage"),
);
// Import layout components
import { Header } from "./layouts/Header";
import { Sidebar } from "./layouts/Sidebar";
import { Footer } from "./layouts/Footer";
import AppRoutes from "./routes/index";
// Inner App Component that uses Router hooks
const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mobileOpen, handleDrawerToggle, setMobileOpen } = useDrawer();
  const [menu, setMenu] = useState<MenuItem[]>([]);
  /** True only after the first menu fetch for the current session completes. */
  const [menuReady, setMenuReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());
  const [user, setUser] = useState<User | null>(getStoredUser());
  const [loading, setLoading] = useState(true);
  // Study context state - fetched from API
  const [selectedStudyId, setSelectedStudyId] = useState<string>(
    () => localStorage.getItem("selectedStudyId") ?? "",
  );
  const [availableStudies, setAvailableStudies] = useState<Study[]>([]);
  const [userStudyRoles, setUserStudyRoles] = useState<Record<string, string>>(
    {},
  );
  const [isStudyChanging, setIsStudyChanging] = useState(false);
  const [studyChangeMessage, setStudyChangeMessage] = useState<string>("");

  const applyStudyContext = useCallback(
    async (
      studiesList: Awaited<
        ReturnType<typeof container.studyUseCase.getAllStudies>
      >,
      activeUser: User,
      options?: { selectStudyId?: string },
    ) => {
      const roles = buildUserStudyRolesMap(activeUser, studiesList);
      const contextStudies = buildAvailableContextStudies(
        studiesList,
        roles,
        activeUser,
      );

      setUserStudyRoles(roles);
      setAvailableStudies(contextStudies);

      let studyToSelect = options?.selectStudyId ?? selectedStudyId ?? "";
      if (studyToSelect && !roles[studyToSelect]) {
        studyToSelect = "";
      }
      if (!studyToSelect && contextStudies.length > 0) {
        studyToSelect = contextStudies[0]?.id ?? "";
      }

      if (studyToSelect) {
        setSelectedStudyId(studyToSelect);
        localStorage.setItem("selectedStudyId", studyToSelect);
        dispatchStudyChanged();

        const roleForStudy = roles[studyToSelect];
        if (roleForStudy) {
          const items = await MenuRepository.fetchMenuItems(roleForStudy);
          setMenu(items);
          return { studyToSelect, menuFetched: true, menuItems: items };
        }
      }

      const platformRole = getPlatformRoleName(activeUser);
      if (platformRole) {
        // PM / CTA with no studies: enter restricted "No Study Selected" mode
        if (isPlatformOnlyRole(platformRole) && contextStudies.length === 0) {
          const restrictedMenu = isPmRole(platformRole)
            ? PM_NO_STUDY_MENU
            : CTA_NO_STUDY_MENU;
          setSelectedStudyId(NO_STUDY_SENTINEL);
          localStorage.setItem("selectedStudyId", NO_STUDY_SENTINEL);
          setMenu(restrictedMenu);
          return {
            studyToSelect: NO_STUDY_SENTINEL,
            menuFetched: true,
            menuItems: restrictedMenu,
          };
        }
        const items = await MenuRepository.fetchMenuItems(platformRole);
        setMenu(items);
        return { studyToSelect, menuFetched: true, menuItems: items };
      }

      return { studyToSelect, menuFetched: false, menuItems: [] as MenuItem[] };
    },
    [selectedStudyId],
  );

  const refreshStudyContext = useCallback(
    async (detail?: StudyContextRefreshDetail) => {
      if (!user || user.isAdmin) {
        return;
      }

      let activeUser = user;
      if (detail?.appendRoleForStudy) {
        const { studyId, role } = detail.appendRoleForStudy;
        activeUser = appendStudyRoleToUser(user, studyId, role);
        setUser(activeUser);
        setStoredUser(activeUser);
      }

      try {
        const studiesList = await container.studyUseCase.getAllStudies({
          assignedOnly: true,
        });
        await applyStudyContext(studiesList, activeUser, {
          selectStudyId: detail?.selectStudyId,
        });
      } catch {
        setAvailableStudies([]);
      }
    },
    [user, applyStudyContext],
  );

  useEffect(() => {
    const onRefresh = (event: Event) => {
      const detail = (event as CustomEvent<StudyContextRefreshDetail>).detail;
      void refreshStudyContext(detail);
    };
    window.addEventListener(STUDY_CONTEXT_REFRESH_EVENT, onRefresh);
    return () => {
      window.removeEventListener(STUDY_CONTEXT_REFRESH_EVENT, onRefresh);
    };
  }, [refreshStudyContext]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapSession = async () => {
      if (!isAuthenticated || !user) {
        setMenuReady(false);
        setLoading(false);
        return;
      }

      // Do not enforce menu access until this fetch finishes
      setMenuReady(false);
      setLoading(true);

      try {
        if (user.isAdmin) {
          const items = await MenuRepository.fetchMenuItems("Tenant Admin");
          if (!cancelled) {
            setMenu(items);
          }
        } else {
          const studiesList = await container.studyUseCase.getAllStudies({
            assignedOnly: true,
          });
          if (!cancelled) {
            await applyStudyContext(studiesList, user);
          }
        }
      } catch {
        if (!cancelled) {
          setAvailableStudies([]);
        }
      } finally {
        if (!cancelled) {
          setMenuReady(true);
          setLoading(false);
        }
      }
    };

    void bootstrapSession();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);
  const handleLogin = useCallback(
    async (userData: User, passwordExpiry?: PasswordExpiryInfo) => {
      if (passwordExpiry?.warningMessage) {
        toast.warning(passwordExpiry.warningMessage, { duration: 6000 });
      }
      try {
        if (userData.isAdmin) {
          // For Tenant admins, fetch menu and then authenticate
          const items = await MenuRepository.fetchMenuItems("Tenant Admin");
          setMenu(items);
          setMenuReady(true);
          setUser(userData);
          setIsAuthenticated(true);
          // Navigate to first menu item after admin login
          const firstPath = getFirstMenuPath(items);
          if (firstPath) {
            setTimeout(() => {
              navigate(firstPath);
            }, 100);
          }
          void clearServiceWorkerAndCacheStorage().catch(() => undefined);
        } else {
          // For non-admin users, fetch studies, roles, and menu before authenticating
          const studiesList = await container.studyUseCase.getAllStudies({
            assignedOnly: true,
          });
          const roles = buildUserStudyRolesMap(userData, studiesList);
          const userStudiesWithRoles = buildAvailableContextStudies(
            studiesList,
            roles,
            userData,
          );
          const platformRole = getPlatformRoleName(userData);

          let menuFetched = false;
          let errorMessage = "";

          if (userStudiesWithRoles.length === 0 && !platformRole) {
            if (Object.keys(roles).length === 0) {
              errorMessage =
                "No roles assigned for this user. Please contact your administrator.";
            } else if (studiesList.length === 0) {
              errorMessage =
                "No studies found for this user. Please contact your administrator.";
            } else {
              errorMessage =
                "No roles assigned for any available studies. Please contact your administrator.";
            }
          } else {
            try {
              const { menuFetched: fetched, menuItems } =
                await applyStudyContext(studiesList, userData);
              menuFetched = fetched;
              if (menuFetched && menuItems.length > 0) {
                const firstPath = getFirstMenuPath(menuItems);
                if (firstPath) {
                  setTimeout(() => {
                    navigate(firstPath);
                  }, 100);
                }
              }
            } catch (menuError) {
              errorMessage =
                menuError instanceof Error
                  ? menuError.message
                  : "Failed to load study context";
            }
          }
          // Only authenticate if menu was successfully fetched
          if (menuFetched) {
            setMenuReady(true);
            setUser(userData);
            setIsAuthenticated(true);
            void clearServiceWorkerAndCacheStorage().catch(() => undefined);
          } else {
            throw new Error(
              errorMessage || "Failed to fetch menu for selected study",
            );
          }
        }
      } catch {
        setUser(null);
        setIsAuthenticated(false);
        setMenu([]);
        setMenuReady(false);
        setAvailableStudies([]);
        setUserStudyRoles({});
      }
    },
    [navigate, applyStudyContext],
  );
  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    clearToken();
    setMenu([]);
    setMenuReady(false);
    // reset UI-only study context
    setSelectedStudyId("");
    setAvailableStudies([]);
    setUserStudyRoles({});
    // Reset drawer state
    setMobileOpen(false);
    navigate("/login");
  }, [setMobileOpen, navigate]);
  // Force-logout after 15 minutes of inactivity; show warning dialog at 14 minutes
  const { showWarning, secondsRemaining, stayLoggedIn } = useInactivityLogout(
    isAuthenticated,
    handleLogout,
  );
  const handleStudyChange = useCallback(
    async (studyId: string) => {
      setIsStudyChanging(true);
      setStudyChangeMessage("Loading new study...");
      setSelectedStudyId(studyId);
      localStorage.setItem("selectedStudyId", studyId);
      dispatchStudyChanged();
      // Close the sidebar when study changes for better UX
      if (mobileOpen) {
        handleDrawerToggle();
      }

      // Handle "No Study Selected" for PM / CTA
      if (studyId === NO_STUDY_SENTINEL) {
        // Use any PM/CTA role from the user (platform-level OR study-scoped)
        const anyRole = user ? getAnyPmOrCtaRole(user) : undefined;
        const restrictedMenu = isPmRole(anyRole)
          ? PM_NO_STUDY_MENU
          : CTA_NO_STUDY_MENU;
        setMenu(restrictedMenu);
        setTimeout(() => {
          setIsStudyChanging(false);
          setStudyChangeMessage("");
          const firstPath = getFirstMenuPath(restrictedMenu);
          if (firstPath) {
            navigate(firstPath);
          }
        }, 300);
        return;
      }

      try {
        // Fetch menu for the new study based on user's role
        const roleForStudy = userStudyRoles[studyId];
        if (!roleForStudy) {
          throw new Error(`No role found for study ${studyId}`);
        }
        setStudyChangeMessage("Fetching menu...");
        const items = await MenuRepository.fetchMenuItems(roleForStudy);
        setMenu(items);
        setStudyChangeMessage("Loading dashboard...");
        // Small delay to ensure menu is set before rendering
        const currentPath = location.pathname;
        setTimeout(() => {
          setIsStudyChanging(false);
          setStudyChangeMessage("");
          // Stay on the current page if it exists in the new role's menu
          if (isPathInMenu(items, currentPath)) {
            // Already there — data will re-fetch via selectedStudyId change
            return;
          }
          // Otherwise fall back to first navigable menu item
          const firstPath = getFirstMenuPath(items);
          if (firstPath) {
            navigate(firstPath);
          } else {
            navigate("/dashboard");
          }
        }, 500);
      } catch {
        // Keep the previous menu so MenuAccessGuard does not fail-open
        // (empty menu + menuReady) and so the sidebar stays usable.
        setIsStudyChanging(false);
        setStudyChangeMessage("");
      }
    },
    [
      userStudyRoles,
      mobileOpen,
      handleDrawerToggle,
      navigate,
      location.pathname,
      user,
    ],
  );
  // Public paths: show their pages immediately without waiting for auth/loading, so invite/register links work
  const searchParams = new URLSearchParams(location.search);
  const isCdaPiEmailDeepLink =
    location.pathname === "/pi/cda-management" &&
    searchParams.get("cdapdf") === "1" &&
    !!searchParams.get("cdaRequestId");
  const isPublicPath =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/reset-password" ||
    location.pathname === "/change-expired-password" ||
    location.pathname.startsWith("/feasibility-questionnaire/") ||
    isCdaPiEmailDeepLink;
  const showLoading = (loading || isStudyChanging) && !isPublicPath;
  if (showLoading) {
    return (
      <ClinicalThemeProvider>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            backgroundColor: "#F8FAFC",
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          {studyChangeMessage && (
            <Box
              sx={{
                fontSize: "1rem",
                color: "#64748B",
                fontWeight: 500,
              }}
            >
              {studyChangeMessage}
            </Box>
          )}
        </Box>
      </ClinicalThemeProvider>
    );
  }
  if (isCdaPiEmailDeepLink) {
    const rid = searchParams.get("cdaRequestId");
    if (!rid) {
      return (
        <ClinicalThemeProvider>
          <CssBaseline />
          <Navigate to="/login" replace />
        </ClinicalThemeProvider>
      );
    }
    return (
      <ClinicalThemeProvider>
        <CssBaseline />
        <Navigate to={`/cda/sign/${rid}`} replace />
      </ClinicalThemeProvider>
    );
  }
  return (
    <ClinicalThemeProvider>
      <CssBaseline />
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage
                onLogin={(userData, passwordExpiry) => {
                  void handleLogin(userData, passwordExpiry);
                }}
              />
            )
          }
        />
        <Route
          path="/register"
          element={
            // Always allow access to /register when an invitation token is present.
            // A user with an old session in localStorage would otherwise be
            // redirected to /dashboard, never seeing the registration form.
            isAuthenticated &&
            !new URLSearchParams(location.search).get("token") ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <RegistrationPage
                onLogin={(userData) => {
                  void handleLogin(userData);
                }}
              />
            )
          }
        />
        <Route
          path="/reset-password"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <ResetPasswordPage onBackToLogin={() => undefined} />
            )
          }
        />
        <Route
          path="/change-expired-password"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <ChangeExpiredPasswordPage />
            )
          }
        />
        {/* Standalone Admin Tools Route - easily removable (delete this Route and AdminTools import) */}
        <Route path="/admin-tools/invite" element={<TenantAdminInvitePage />} />
        {/* Public external CDA signing portal — accessible without authentication */}
        <Route
          path="/cda/sign/:id"
          element={
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <CDASignPage />
            </Suspense>
          }
        />
        {/* Public PI-facing site feasibility questionnaire */}
        <Route
          path="/feasibility-questionnaire/:recordId"
          element={
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <FeasibilityQuestionnairePage />
            </Suspense>
          }
        />
        <Route
          path="/response/feasibility-questionnaire/:recordId"
          element={
            isAuthenticated && user ? (
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "100vh",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                <FeasibilityQuestionnaireResponsePage />
              </Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/*"
          element={
            isAuthenticated && user ? (
              <Box sx={{ display: "flex", minHeight: "100vh" }}>
                <Header
                  onMenuClick={handleDrawerToggle}
                  user={user}
                  onLogout={handleLogout}
                  availableStudies={availableStudies}
                  selectedStudyId={selectedStudyId}
                  userStudyRoles={userStudyRoles}
                  onStudyChange={(studyId) => {
                    void handleStudyChange(studyId);
                  }}
                />
                <Sidebar
                  menu={menu}
                  open={mobileOpen}
                  onClose={() => setMobileOpen(false)}
                  variant="temporary"
                />
                <Footer />
                <AppRoutes
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  user={user}
                  onLogout={handleLogout}
                  selectedStudyId={selectedStudyId}
                  availableStudies={availableStudies}
                  userStudyRoles={userStudyRoles}
                  menu={menu}
                  menuReady={menuReady}
                  sx={{ pt: 8, pb: { xs: 8, md: 6 } }}
                />
              </Box>
            ) : location.pathname === "/register" ? (
              <RegistrationPage
                onLogin={(userData) => {
                  void handleLogin(userData);
                }}
              />
            ) : location.pathname === "/reset-password" ? (
              <ResetPasswordPage onBackToLogin={() => undefined} />
            ) : location.pathname === "/change-expired-password" ? (
              <ChangeExpiredPasswordPage />
            ) : new URLSearchParams(location.search).get("cdapdf") === "1" &&
              new URLSearchParams(location.search).get("cdaRequestId") ? (
              // Email CDA sign links — redirect directly to the public signing
              // page without requiring authentication
              <Navigate
                to={`/cda/sign/${new URLSearchParams(location.search).get("cdaRequestId")}`}
                replace
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
      <PWAUpdateNotification />
      <InactivityWarningDialog
        open={showWarning}
        secondsRemaining={secondsRemaining}
        onStayLoggedIn={stayLoggedIn}
      />
    </ClinicalThemeProvider>
  );
};
// Main App Component with Router
const App: React.FC = () => {
  return (
    <ClinicalThemeProvider>
      <CssBaseline />
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AppContent />
      </Router>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            zIndex: 9999,
          },
        }}
      />
    </ClinicalThemeProvider>
  );
};
export default App;
